import {
  Button,
  Drawer,
  Form,
  FormInstance,
  Input,
  InputNumber,
  message,
  Pagination,
  Select,
  Space,
  Table,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  addPincode,
  fetchDeliveryZones,
  fetchPincodes,
  fetchZoneChoices,
  updatePincode,
} from "../../../apis/branch.api";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";

interface PincodeRecord {
  id: number;
  pincode: number;
  zone: string;
  delivery_zone: number | null;
  delivery_zone_name: string | null;
  ln_client_code: string | null;
}

const SubmitButton = ({
  form,
  loading,
}: {
  form: FormInstance;
  loading?: boolean;
}) => {
  const [submittable, setSubmittable] = React.useState(false);
  const values = Form.useWatch([], form);

  React.useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => setSubmittable(true),
      () => setSubmittable(false)
    );
  }, [form, values]);

  return (
    <Button
      type="primary"
      htmlType="submit"
      disabled={!submittable || loading}
      loading={loading}
    >
      Submit
    </Button>
  );
};

const PincodeList = () => {
  const { hasPermission } = usePermissions();
  const [pincodes, setPincodes] = useState<PincodeRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [zoneFilter, setZoneFilter] = useState<string | undefined>(undefined);
  const [deliveryZoneFilter, setDeliveryZoneFilter] = useState<
    number | undefined
  >(undefined);

  const [zoneChoices, setZoneChoices] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [deliveryZones, setDeliveryZones] = useState<
    Array<{ id: number; zone: string }>
  >([]);

  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [selectedPincode, setSelectedPincode] = useState<PincodeRecord | null>(
    null
  );
  const [reloadTick, setReloadTick] = useState(0);

  // Debounce search
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, zoneFilter, deliveryZoneFilter]);

  // Load zone choices and delivery zones
  useEffect(() => {
    let cancelled = false;
    fetchZoneChoices().then((res: any) => {
      if (!cancelled) setZoneChoices(res ?? []);
    });
    fetchDeliveryZones().then((res: any) => {
      if (!cancelled) setDeliveryZones(res ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load pincodes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params: any = { page, page_size: pageSize };
    if (debouncedSearch) params.search = debouncedSearch;
    if (zoneFilter) params.zone = zoneFilter;
    if (deliveryZoneFilter) params.delivery_zone = deliveryZoneFilter;

    fetchPincodes(params)
      .then((res: any) => {
        if (cancelled) return;
        setPincodes(res?.results ?? []);
        setTotal(res?.count ?? 0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, debouncedSearch, zoneFilter, deliveryZoneFilter, reloadTick]);

  const onAddSaved = useCallback(() => {
    setAddDrawerOpen(false);
    setReloadTick((t) => t + 1);
  }, []);

  const onEditSaved = useCallback(() => {
    setEditDrawerOpen(false);
    setReloadTick((t) => t + 1);
  }, []);

  const columns: ColumnsType<PincodeRecord> = useMemo(
    () => [
      {
        title: "Pincode",
        dataIndex: "pincode",
        key: "pincode",
        width: 120,
      },
      {
        title: "Zone",
        dataIndex: "zone",
        key: "zone",
        width: 140,
        render: (val: string) => {
          const match = zoneChoices.find((z) => z.value === val);
          return match?.label ?? val ?? "N/A";
        },
      },
      {
        title: "Delivery Zone",
        dataIndex: "delivery_zone_name",
        key: "delivery_zone_name",
        width: 160,
        render: (val: string | null) => val ?? "N/A",
      },
      {
        title: "LN Client Code",
        dataIndex: "ln_client_code",
        key: "ln_client_code",
        width: 160,
        render: (val: string | null) => val ?? "N/A",
      },
      {
        title: "Action",
        key: "action",
        width: 120,
        fixed: "right" as const,
        render: (_: any, record: PincodeRecord) => (
          <Button
            size="small"
            className="fw-bold"
            type="primary"
            disabled={!hasPermission("branch_details.update")}
            onClick={() => {
              setSelectedPincode(record);
              setEditDrawerOpen(true);
            }}
          >
            Update
          </Button>
        ),
      },
    ],
    [zoneChoices]
  );

  return (
    <div className="p-3 py-2">
      <div className="d-flex justify-content-between align-items-center pb-3">
        <div className="th-font-size-18 th-font-weight-600 primary-color-text align-middle">
          <div>Pincodes</div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Select
            style={{ minWidth: 160 }}
            placeholder="Filter by zone"
            value={zoneFilter}
            onChange={(v) => setZoneFilter(v)}
            options={zoneChoices}
            allowClear
            showSearch
            optionFilterProp="label"
          />
          <Select
            style={{ minWidth: 180 }}
            placeholder="Filter by delivery zone"
            value={deliveryZoneFilter}
            onChange={(v) => setDeliveryZoneFilter(v)}
            options={deliveryZones.map((dz) => ({
              label: dz.zone,
              value: dz.id,
            }))}
            allowClear
            showSearch
            optionFilterProp="label"
          />
          <Input.Search
            type="text"
            style={{ maxWidth: 200, minWidth: 160 }}
            placeholder="Search pincode"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            allowClear
          />
          <Button
            size="middle"
            className="fw-bold"
            type="primary"
            disabled={!hasPermission("branch_details.create")}
            onClick={() => setAddDrawerOpen(true)}
          >
            Add Pincode
          </Button>
        </div>
      </div>
      <div className="row gap-3">
        <Table
          columns={columns}
          dataSource={pincodes}
          pagination={false}
          loading={loading}
          rowKey="id"
          scroll={{ x: "100%" }}
        />
      </div>
      <Pagination
        current={page}
        total={total}
        showSizeChanger={false}
        pageSize={pageSize}
        className="mt-3"
        onChange={(p) => setPage(p)}
      />

      {/* Add Pincode Drawer */}
      <Drawer
        title="Add Pincode"
        placement="right"
        closable={false}
        onClose={() => setAddDrawerOpen(false)}
        open={addDrawerOpen}
        destroyOnClose
      >
        <PincodeCreateForm
          onSaved={onAddSaved}
          zoneChoices={zoneChoices}
          deliveryZones={deliveryZones}
        />
      </Drawer>

      {/* Edit Pincode Drawer */}
      <Drawer
        title="Update Pincode"
        placement="right"
        closable={false}
        onClose={() => setEditDrawerOpen(false)}
        open={editDrawerOpen}
        destroyOnClose
      >
        <PincodeEditForm
          onSaved={onEditSaved}
          selectedPincode={selectedPincode}
          zoneChoices={zoneChoices}
          deliveryZones={deliveryZones}
        />
      </Drawer>
    </div>
  );
};

// --- Create Form ---
interface PincodeFormProps {
  onSaved: () => void;
  zoneChoices: Array<{ value: string; label: string }>;
  deliveryZones: Array<{ id: number; zone: string }>;
}

const PincodeCreateForm = ({
  onSaved,
  zoneChoices,
  deliveryZones,
}: PincodeFormProps) => {
  const [form] = Form.useForm();
  const { hasPermission } = usePermissions();
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values: any) => {
    if (!hasPermission("branch_details.create")) {
      message.error("You don't have permission to add a pincode");
      return;
    }
    setSubmitting(true);
    try {
      const ok = await addPincode(values);
      if (ok) onSaved();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form
      form={form}
      name="pincode_create"
      layout="vertical"
      autoComplete="off"
      onFinish={onFinish}
    >
      <Form.Item
        name="pincode"
        label="Pincode"
        rules={[
          { required: true, message: "Enter a pincode" },
          {
            type: "number",
            min: 100000,
            max: 999999,
            message: "Must be a valid 6-digit pincode",
          },
        ]}
      >
        <InputNumber style={{ width: "100%" }} placeholder="Enter 6-digit pincode" />
      </Form.Item>
      <Form.Item name="zone" label="Zone" rules={[{ required: true }]}>
        <Select
          placeholder="Select zone"
          options={zoneChoices}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item name="delivery_zone" label="Delivery Zone" rules={[{ required: true, message: "Select a delivery zone" }]}>
        <Select
          placeholder="Select delivery zone"
          options={deliveryZones.map((dz) => ({
            label: dz.zone,
            value: dz.id,
          }))}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item name="ln_client_code" label="LN Client Code">
        <Input placeholder="Enter LN client code" />
      </Form.Item>
      <Form.Item>
        <Space>
          <SubmitButton form={form} loading={submitting} />
          <Button htmlType="reset">Reset</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

// --- Edit Form ---
interface PincodeEditFormProps extends PincodeFormProps {
  selectedPincode: PincodeRecord | null;
}

const PincodeEditForm = ({
  onSaved,
  selectedPincode,
  zoneChoices,
  deliveryZones,
}: PincodeEditFormProps) => {
  const [form] = Form.useForm();
  const { hasPermission } = usePermissions();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (selectedPincode) {
      form.setFieldsValue({
        zone: selectedPincode.zone,
        delivery_zone: selectedPincode.delivery_zone,
        ln_client_code: selectedPincode.ln_client_code,
      });
    }
  }, [selectedPincode, form]);

  const onFinish = async (values: any) => {
    if (!hasPermission("branch_details.update")) {
      message.error("You don't have permission to update a pincode");
      return;
    }
    setSubmitting(true);
    try {
      const ok = await updatePincode({
        id: selectedPincode?.id,
        pincode: selectedPincode?.pincode,
        ...values,
      });
      if (ok) onSaved();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form
      form={form}
      name="pincode_edit"
      layout="vertical"
      autoComplete="off"
      onFinish={onFinish}
    >
      <Form.Item label="Pincode">
        <Input value={selectedPincode?.pincode} disabled />
      </Form.Item>
      <Form.Item name="zone" label="Zone" rules={[{ required: true }]}>
        <Select
          placeholder="Select zone"
          options={zoneChoices}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item name="delivery_zone" label="Delivery Zone" rules={[{ required: true, message: "Select a delivery zone" }]}>
        <Select
          placeholder="Select delivery zone"
          options={deliveryZones.map((dz) => ({
            label: dz.zone,
            value: dz.id,
          }))}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item name="ln_client_code" label="LN Client Code">
        <Input placeholder="Enter LN client code" />
      </Form.Item>
      <Form.Item>
        <Space>
          <SubmitButton form={form} loading={submitting} />
          <Button htmlType="reset">Reset</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default PincodeList;
